import { createStore } from 'zustand/vanilla';
import { subscribeWithSelector, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { generatePegInID, waitAndDecodeDeposit, waitAndDecodeReceipt, waitForBridge, } from 'lib/peg-in';
import { requestAirdrop } from './airdrop';
export var BridgeState;
(function (BridgeState) {
    BridgeState["idle"] = "idle";
    BridgeState["airdrop"] = "airdrop";
    BridgeState["evmRequestCreated"] = "evmRequestCreated";
    BridgeState["evmRequestPending"] = "evmRequestPending";
    BridgeState["evmRequestCompleted"] = "evmRequestCompleted";
    BridgeState["btcDepositPending"] = "btcDepositPending";
    BridgeState["btcDepositCompleted"] = "btcDepositCompleted";
    BridgeState["bridgeTxPending"] = "bridgeTxPending";
    BridgeState["bridgeTxCompleted"] = "bridgeTxCompleted";
    BridgeState["error"] = "error";
    BridgeState["btcFaucetAirdropPending"] = "btcFaucetAirdropPending";
    BridgeState["btcFaucetAirdropCompleted"] = "btcFaucetAirdropCompleted";
})(BridgeState || (BridgeState = {}));
const bridgeStore = createStore()(persist(subscribeWithSelector(immer(set => ({
    btcFee: 5,
    selectedChainId: null,
    captchaToken: null,
    captchaTokenExpiry: null,
    idempotencyKey: null,
    idempotencyKeyExpiry: null,
    context: {
        state: BridgeState.idle,
        error: null,
        evmRequest: null,
        evmResponse: null,
        btcDeposit: null,
        bridgeTx: null,
        btcFaucetAirdrop: null,
    },
    actions: {
        reset: () => set(state => {
            state.context = {
                state: BridgeState.idle,
                error: null,
                evmRequest: null,
                evmResponse: null,
                btcDeposit: null,
                bridgeTx: null,
                btcFaucetAirdrop: null,
            };
        }),
        beginAirdrop: () => set(state => {
            state.context.state = BridgeState.airdrop;
        }),
        createRequest: (pegInId, amount, address) => set(state => {
            // clean up previous request and set new request data
            state.context = {
                state: BridgeState.evmRequestCreated,
                error: null,
                evmRequest: {
                    pegInId,
                    amount: amount.toString(),
                    address: address.toLowerCase(),
                },
                evmResponse: null,
                btcDeposit: null,
                bridgeTx: null,
                btcFaucetAirdrop: null,
            };
        }),
        setRequestHash: (hash) => set(state => {
            state.context.error = null;
            state.context.evmRequest.hash = hash;
            state.context.state = BridgeState.evmRequestPending;
        }),
        setRequestResponse: (response) => set(state => {
            if (response.status === 'success') {
                state.context.evmResponse = {
                    status: response.status,
                    pegInId: response.pegInId,
                    peginAddress: response.pegInAddress,
                    amount: response.amount.toString(),
                    evmAddress: response.evmAddress,
                    taprootAddress: response.taprootAddress,
                };
                state.context.error = null;
                state.context.state = BridgeState.evmRequestCompleted;
            }
            else if (response.status === 'reverted') {
                state.context.error = 'Transaction reverted';
                state.context.state = BridgeState.error;
            }
        }),
        setDepositTx: (txId, amount) => set(state => {
            state.context.btcDeposit = {
                txId,
                amount: amount.toString(),
                status: 'pending',
            };
            state.context.state = BridgeState.btcDepositPending;
            state.context.error = null;
        }),
        setDepositResponse: (response) => set(state => {
            state.context.btcDeposit.status = response.status;
            if (response.status !== 'success') {
                state.context.error = 'Transaction reverted';
                state.context.state = BridgeState.error;
            }
            else {
                state.context.state = BridgeState.btcDepositCompleted;
                state.context.error = null;
            }
        }),
        setBtcAirdropTx: (txId) => set(state => {
            state.context.btcFaucetAirdrop = {
                txId,
                status: 'pending',
            };
            state.context.state = BridgeState.btcFaucetAirdropPending;
            state.context.error = null;
        }),
        setBtcAirdropResponse: (response) => set(state => {
            state.context.btcFaucetAirdrop.status = response.status;
            if (response.status !== 'success') {
                state.context.error = 'Transaction reverted';
                state.context.state = BridgeState.error;
            }
            else {
                state.context.state = BridgeState.btcFaucetAirdropCompleted;
                state.context.error = null;
            }
        }),
        setBridgeResponse: (response) => set(state => {
            state.context.state = response.ok
                ? BridgeState.bridgeTxCompleted
                : BridgeState.error;
            if (!response.ok) {
                state.context.error = 'Bridge transaction failed';
            }
            else {
                state.context.bridgeTx = {
                    hash: '0xtodo',
                    status: 'success',
                };
            }
        }),
        setBtcFee: (fee) => set(state => {
            state.btcFee = fee;
        }),
        setSelectedChainId: (chainId) => set(state => {
            state.selectedChainId = chainId;
        }),
    },
}))), {
    name: 'bos-bridge-store',
    version: 0,
    partialize: state => ({
        selectedChain: state.selectedChainId,
        context: state.context,
    }),
}));
class Tracker {
    store;
    #abortControllerReq = new AbortController();
    #abortControllerDep = new AbortController();
    #abortControllerBridge = new AbortController();
    constructor(store) {
        this.store = store;
        this.init();
    }
    setIdempotencyKey(key) {
        this.store.setState(s => {
            s.idempotencyKey = key;
            // Set expiration to match CAPTCHA token
            s.idempotencyKeyExpiry = Date.now() + 2 * 60 * 1000;
        });
    }
    getIdempotencyKey() {
        const state = this.store.getState();
        const key = state.idempotencyKey;
        const expiry = state.idempotencyKeyExpiry;
        // If key exists and is not expired, return it
        if (key && expiry && Date.now() < expiry) {
            return key;
        }
        // Key is expired or doesn't exist
        return null;
    }
    isIdempotencyKeyValid() {
        return this.getIdempotencyKey() !== null;
    }
    setCaptchaToken(token) {
        this.store.setState(s => {
            s.captchaToken = token;
            // Set expiration to 2 minutes from now (Cloudflare Turnstile default)
            s.captchaTokenExpiry = Date.now() + 2 * 60 * 1000;
        });
    }
    getCaptchaToken() {
        const state = this.store.getState();
        const token = state.captchaToken;
        const expiry = state.captchaTokenExpiry;
        // If token exists and is not expired, return it
        if (token && expiry && Date.now() < expiry) {
            return token;
        }
        // Token is expired or doesn't exist
        return null;
    }
    isCaptchaTokenValid() {
        return this.getCaptchaToken() !== null;
    }
    async initiateRequest(amount, address) {
        const id = generatePegInID();
        this.store.getState().actions.beginAirdrop();
        await requestAirdrop(this.store.getState().selectedChainId, address);
        this.store.getState().actions.createRequest(id, amount, address);
        return id;
    }
    trackRequest(hash) {
        this.store.getState().actions.setRequestHash(hash);
        this.waitForRequestResponse();
    }
    trackDeposit(txId, amount) {
        this.store.getState().actions.setDepositTx(txId, amount);
        this.waitForDepositResponse();
    }
    trackBtcFaucetAirdrop(txId) {
        this.store.getState().actions.setBtcAirdropTx(txId);
        this.waitForBtcAirdropResponse();
    }
    // todo
    trackBridge() {
        this.store.setState(s => {
            s.context.state = BridgeState.bridgeTxPending;
        });
        this.waitForBridgeResponse();
    }
    setFailure(error) {
        this.store.setState(s => {
            s.context.error = error;
            s.context.state = BridgeState.error;
        });
    }
    // set error message after which user still can retry
    setWarning(error) {
        this.store.setState(s => {
            s.context.error = error;
        });
    }
    clearWarning() {
        this.store.setState(s => {
            s.context.error = null;
        });
    }
    reset() {
        this.#abortControllerBridge.abort();
        this.#abortControllerDep.abort();
        this.#abortControllerReq.abort();
        this.#abortControllerBridge = new AbortController();
        this.#abortControllerDep = new AbortController();
        this.#abortControllerReq = new AbortController();
        this.store.getState().actions.reset();
    }
    waitForRequestResponse() {
        const signal = this.#abortControllerReq.signal;
        waitAndDecodeReceipt(this.store.getState().context.evmRequest.hash)
            .then(result => {
            // ignore if request was reset
            if (signal.aborted)
                return;
            this.store.getState().actions.setRequestResponse(result);
        })
            .catch(console.log);
    }
    waitForDepositResponse() {
        const signal = this.#abortControllerDep.signal;
        waitAndDecodeDeposit(this.store.getState().context.btcDeposit.txId, signal)
            .then(result => {
            if (signal.aborted)
                return;
            this.store.getState().actions.setDepositResponse({
                status: result.status.confirmed ? 'success' : 'reverted',
            });
        })
            .catch(console.log);
    }
    waitForBtcAirdropResponse() {
        const signal = this.#abortControllerDep.signal;
        waitAndDecodeDeposit(this.store.getState().context.btcFaucetAirdrop.txId, signal)
            .then(result => {
            if (signal.aborted)
                return;
            this.store.getState().actions.setBtcAirdropResponse({
                status: result.status.confirmed ? 'success' : 'reverted',
            });
        })
            .catch(console.log);
    }
    waitForBridgeResponse() {
        const signal = this.#abortControllerBridge.signal;
        const chainId = this.store.getState().selectedChainId;
        if (!chainId) {
            this.setFailure('Chain not selected');
            return;
        }
        waitForBridge(this.store.getState().context.evmRequest.pegInId, chainId, signal)
            .then(result => {
            if (signal.aborted)
                return;
            if (result.ok) {
                this.store.getState().actions.setBridgeResponse(result);
            }
            else {
                this.setFailure('Bridge transaction failed');
            }
        })
            .catch(console.log);
    }
    // populate with data after page refresh
    init() {
        const state = this.store.getState();
        fetch('https://mempool.space/testnet/api/v1/fees/recommended')
            .then(r => r.json())
            .then(r => r.economyFee ?? 5)
            .then(state.actions.setBtcFee)
            .catch(console.warn);
        if (state.context.state === BridgeState.evmRequestPending &&
            state.context.evmRequest?.hash) {
            this.waitForRequestResponse();
            return;
        }
        if (state.context.state === BridgeState.btcDepositPending &&
            state.context.btcDeposit?.txId) {
            this.waitForDepositResponse();
            return;
        }
        if ([BridgeState.btcDepositCompleted, BridgeState.bridgeTxPending].includes(state.context.state) &&
            state.context.evmRequest?.pegInId) {
            this.trackBridge();
        }
    }
}
export const tracker = new Tracker(bridgeStore);
